<template>
  <div class="min-h-screen bg-[#FFF7EF]">
    <div class="mx-auto max-w-6xl px-4 py-12 lg:px-8">
      <div class="grid gap-8 lg:grid-cols-[1fr_1fr]">
        <section class="rounded-3xl border border-gray-100 bg-white p-8 shadow-sm">
          <RouterLink class="back-home-link" to="/">
            <i class="ri-arrow-left-line"></i>
            <span>Back to home</span>
          </RouterLink>

          <h1 class="text-3xl font-semibold text-ink">Create your Invi8 account</h1>
          <p class="mt-2 text-sm text-muted">Join as a user or switch to vendor onboarding.</p>

          <AuthRoleTabs
            class="mt-6"
            active-role="user"
            right-to="/vendor-signup?role=vendor"
            @select="resetForm"
          />

          <form class="mt-6 space-y-4" @submit.prevent="handleSubmit">
            <p v-if="error" class="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-xs font-semibold text-red-700">
              {{ error }}
            </p>

            <label class="field-block">
              <span class="sr-only">Full Name</span>
              <input
                v-model.trim="form.fullName"
                type="text"
                required
                placeholder="Full Name"
                class="register-field"
              >
            </label>

            <label class="field-block">
              <span class="sr-only">Email</span>
              <input
                v-model.trim="form.email"
                type="email"
                required
                placeholder="Email"
                class="register-field"
              >
            </label>

            <label class="field-block">
              <span class="sr-only">Username</span>
              <input
                v-model.trim="form.username"
                type="text"
                required
                placeholder="Username"
                class="register-field"
              >
            </label>

            <label class="field-block">
              <span class="sr-only">Password</span>
              <div class="password-shell">
                <input
                  v-model="form.password"
                  :type="showPassword ? 'text' : 'password'"
                  required
                  placeholder="Password"
                  class="register-field register-field--with-action"
                >
                <div class="password-actions">
                  <button
                    type="button"
                    class="icon-btn"
                    @click="generatePassword"
                    aria-label="Generate password"
                    title="Generate password"
                  >
                    <i class="ri-shuffle-line"></i>
                  </button>
                  <button
                    type="button"
                    class="icon-btn"
                    @click="showPassword = !showPassword"
                    aria-label="Toggle password visibility"
                    title="Toggle password visibility"
                  >
                    <i :class="showPassword ? 'ri-eye-off-line' : 'ri-eye-line'"></i>
                  </button>
                </div>
              </div>
            </label>

            <div class="strength-card">
              <div class="strength-head">
                <strong>Password strength:</strong>
                <span>{{ passwordStrengthLabel }}</span>
              </div>
              <div class="strength-bar">
                <span :style="{ width: `${passwordStrengthScore * 20}%` }"></span>
              </div>
              <div class="strength-rules">
                <p :class="ruleClass(passwordChecks.minLength)">At least 8 characters</p>
                <p :class="ruleClass(passwordChecks.uppercase)">Contains uppercase letter</p>
                <p :class="ruleClass(passwordChecks.lowercase)">Contains lowercase letter</p>
                <p :class="ruleClass(passwordChecks.number)">Contains number</p>
                <p :class="ruleClass(passwordChecks.special)">Contains special character</p>
              </div>
            </div>

            <label class="field-block">
              <span class="sr-only">Confirm Password</span>
              <div class="password-shell">
                <input
                  v-model="form.confirmPassword"
                  :type="showConfirmPassword ? 'text' : 'password'"
                  required
                  placeholder="Confirm Password"
                  class="register-field register-field--with-action"
                >
                <div class="password-actions">
                  <button
                    type="button"
                    class="icon-btn"
                    @click="showConfirmPassword = !showConfirmPassword"
                    aria-label="Toggle confirm password visibility"
                    title="Toggle confirm password visibility"
                  >
                    <i :class="showConfirmPassword ? 'ri-eye-off-line' : 'ri-eye-line'"></i>
                  </button>
                </div>
              </div>
            </label>

            <button
              type="submit"
              :disabled="loading"
              class="register-submit"
            >
              {{ loading ? 'Registering...' : 'Register' }}
            </button>
          </form>

          <p class="mt-6 text-center text-xs text-muted">
            Already have an account?
            <RouterLink class="font-semibold text-brand" to="/login?role=user">Log in</RouterLink>
          </p>
        </section>

        <section class="rounded-3xl border border-orange-100 bg-white/90 p-8 shadow-sm">
          <p class="text-xs font-semibold uppercase tracking-[0.2em] text-muted">Start strong</p>
          <h2 class="mt-3 text-2xl font-semibold text-ink">Plan faster with a personal Invi8 account</h2>
          <div class="mt-6 space-y-4 text-sm text-muted">
            <div class="flex items-start gap-3">
              <span class="mt-1 inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#FFF3E5] text-lg">✓</span>
              <div>
                <p class="font-semibold text-ink">Save venues and vendors</p>
                <p class="text-xs">Keep your shortlist ready while comparing options.</p>
              </div>
            </div>
            <div class="flex items-start gap-3">
              <span class="mt-1 inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#FFF3E5] text-lg">✓</span>
              <div>
                <p class="font-semibold text-ink">Send inquiries faster</p>
                <p class="text-xs">Reuse your event details instead of filling forms repeatedly.</p>
              </div>
            </div>
            <div class="flex items-start gap-3">
              <span class="mt-1 inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#FFF3E5] text-lg">✓</span>
              <div>
                <p class="font-semibold text-ink">Track responses in one place</p>
                <p class="text-xs">Manage venue replies and follow-ups from a single account.</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import AuthRoleTabs from '@/components/user/AuthRoleTabs.vue'
import api from '@/services/user/api'
import { setAuthToken } from '@/services/auth'
import { useSessionStore } from '@/stores/session'
import { extractApiErrorMessage } from '@/utils/apiErrors'

const router = useRouter()
const session = useSessionStore()

const form = reactive({
  fullName: '',
  email: '',
  username: '',
  password: '',
  confirmPassword: ''
})

const loading = ref(false)
const error = ref('')
const showPassword = ref(false)
const showConfirmPassword = ref(false)

const passwordChecks = computed(() => ({
  minLength: form.password.length >= 8,
  uppercase: /[A-Z]/.test(form.password),
  lowercase: /[a-z]/.test(form.password),
  number: /\d/.test(form.password),
  special: /[^A-Za-z0-9]/.test(form.password)
}))

const passwordStrengthScore = computed(() =>
  Object.values(passwordChecks.value).filter(Boolean).length
)

const passwordStrengthLabel = computed(() => {
  if (!form.password) return 'Enter A Password'
  if (passwordStrengthScore.value <= 2) return 'Weak'
  if (passwordStrengthScore.value <= 4) return 'Medium'
  return 'Strong'
})

const resolveUserId = (user) =>
  user?.s_id ||
  user?.id ||
  user?.user_id ||
  user?.vendor_id ||
  user?.vendorId ||
  ''

const ruleClass = (passed) => ({
  'rule-item': true,
  'rule-item--pass': passed,
  'rule-item--fail': !passed
})

const getClientTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

const getClientZoneLabel = () => {
  const offsetMinutes = -new Date().getTimezoneOffset()
  const sign = offsetMinutes >= 0 ? '+' : '-'
  const absoluteMinutes = Math.abs(offsetMinutes)
  const hours = Math.floor(absoluteMinutes / 60)
  const minutes = absoluteMinutes % 60
  return `GMT${sign}${hours}:${String(minutes).padStart(2, '0')}`
}

const generatePassword = () => {
  const generated = 'Invi8@' + Math.random().toString(36).slice(-6) + '9A'
  form.password = generated
  form.confirmPassword = generated
}

const resetForm = () => {
  form.fullName = ''
  form.email = ''
  form.username = ''
  form.password = ''
  form.confirmPassword = ''
  error.value = ''
  showPassword.value = false
  showConfirmPassword.value = false
}

const handleSubmit = async () => {
  error.value = ''

  if (!form.fullName || !form.email || !form.username || !form.password || !form.confirmPassword) {
    error.value = 'All required fields must be filled'
    return
  }

  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
    error.value = 'Enter a valid email address'
    return
  }

  if (form.password.length < 8) {
    error.value = 'Password must be at least 8 characters'
    return
  }

  if (passwordStrengthScore.value < 5) {
    error.value = 'Password must include uppercase, lowercase, number and special character'
    return
  }

  if (form.password !== form.confirmPassword) {
    error.value = 'Passwords do not match'
    return
  }

  loading.value = true

  const payload = new FormData()
  payload.append('name', form.fullName)
  payload.append('email', form.email)
  payload.append('username', form.username)
  payload.append('password', form.password)
  payload.append('timezone', getClientTimezone())
  payload.append('zone', getClientZoneLabel())
  payload.append('source', 'web')
  payload.append('user_type', 'user')

  try {
    const { data } = await api.register(payload)
    const isError = data?.status === 0 || data?.status === 'error' || data?.success === false

    if (isError) {
      throw {
        response: { data },
        message: extractApiErrorMessage({ response: { data } }, 'Registration failed')
      }
    }

    const token = data?.token || data?.access_token || data?.data?.token || data?.data?.access_token
    const responseItems = Array.isArray(data?.data) ? data.data : []
    const responseData = data?.data && typeof data.data === 'object' && !Array.isArray(data.data) ? data.data : null
    const user =
      data?.user ||
      responseItems[0] ||
      data?.data?.user ||
      data?.data?.profile ||
      data?.profile ||
      responseData ||
      {}

    const resolvedUserId = resolveUserId(user) || resolveUserId(data)
    if (!resolvedUserId) {
      throw new Error('Registration succeeded but no user ID was returned.')
    }

    if (token) {
      setAuthToken(token)
    }

    session.setUser({
      ...user,
      name: user?.name || user?.s_name || form.fullName,
      s_name: user?.s_name || user?.name || form.fullName,
      email: user?.email || user?.s_email || form.email,
      s_email: user?.s_email || user?.email || form.email,
      username: user?.username || user?.s_username || form.username,
      s_username: user?.s_username || user?.username || form.username,
      s_id: resolvedUserId || user?.s_id || '',
      role: 'user',
      type: 'user'
    })

    router.push('/host')
  } catch (err) {
    error.value = extractApiErrorMessage(err, 'Registration failed')
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.back-home-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 18px;
  font-size: 13px;
  font-weight: 700;
  color: #ec2263;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.field-block {
  display: block;
}

.register-field {
  width: 100%;
  border: 1px solid #e8e0d8;
  border-radius: 18px;
  background: #fff;
  padding: 16px 18px;
  font-size: 16px;
  color: #4b5563;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.register-field:focus {
  outline: none;
  border-color: #ec2263;
  box-shadow: 0 0 0 4px rgba(236, 34, 99, 0.08);
}

.register-field--with-action {
  padding-right: 88px;
}

.password-shell {
  position: relative;
}

.password-actions {
  position: absolute;
  top: 50%;
  right: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  transform: translateY(-50%);
}

.icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: #64748b;
}

.strength-card {
  border: 1px solid #f1ece7;
  border-radius: 18px;
  background: #faf9f8;
  padding: 14px 14px 12px;
}

.strength-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 14px;
  color: #334155;
}

.strength-head span {
  font-weight: 600;
  color: #64748b;
}

.strength-bar {
  margin-top: 12px;
  height: 6px;
  border-radius: 999px;
  background: #e5e7eb;
  overflow: hidden;
}

.strength-bar span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #fb923c 0%, #f97316 55%, #ec2263 100%);
  transition: width 0.2s ease;
}

.strength-rules {
  margin-top: 12px;
  display: grid;
  gap: 6px;
}

.rule-item {
  margin: 0;
  font-size: 14px;
}

.rule-item::before {
  content: '×';
  margin-right: 8px;
  font-weight: 700;
}

.rule-item--fail {
  color: #8b93a1;
}

.rule-item--pass {
  color: #0f766e;
}

.rule-item--pass::before {
  content: '✓';
}

.register-submit {
  width: 100%;
  border: 0;
  border-radius: 999px;
  background: #f58026;
  padding: 15px 18px;
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  box-shadow: 0 10px 24px rgba(255, 168, 103, 0.24);
  transition: background 0.2s ease, transform 0.2s ease;
}

.register-submit:hover:not(:disabled) {
  background: #ff9850;
  transform: translateY(-1px);
}

.register-submit:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}
</style>
